/**
 * Lets the OIDC callback hand control back to the router instead of around it.
 *
 * When the code exchange finishes, the library sends the browser to wherever the login
 * started from. Its default is `history.replaceState`, which changes the address bar
 * without emitting a popstate, so react-router keeps rendering the callback route while
 * the URL claims otherwise. Routing through `navigate` keeps the two in agreement.
 *
 * A module-level holder rather than a prop, because the library calls back from inside an
 * effect where a hook cannot be used.
 */

let navigate: ((path: string) => void) | null = null;

export function setNavigate(fn: ((path: string) => void) | null): void {
  navigate = fn;
}

/**
 * Strip the deployment's base path, which react-router's basename adds back.
 *
 * The library reports an absolute path including the base (`/apps/mikeai/assistant`),
 * and passing that to a router already mounted at `/apps/mikeai/` produces the path
 * twice.
 */
export function toRoutePath(path: string, base: string = import.meta.env.BASE_URL): string {
  if (base === '/' || !path.startsWith(base)) return path || '/';
  return path.slice(base.length - 1) || '/';
}

export async function navigateAfterCallback(callbackPath: string): Promise<void> {
  const target = toRoutePath(callbackPath || '/');
  if (navigate) navigate(target);
  else window.history.replaceState(null, '', callbackPath);

  // Changing the URL is not enough to leave the callback screen. The library decides
  // which route it is on from a piece of React state it seeds once on mount and refreshes
  // only when a popstate arrives, and neither history.replaceState nor a router navigate
  // emits one. Without this the address bar says the app while the page still says "you
  // will be redirected", until the user reloads by hand.
  //
  // The event is a no-op for the router, which re-reads the same location it just wrote.
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
}
